import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Sounds} from '../enums/Sounds.js';
import {AudioManager, globalAudioManager} from '@wonderlandengine/spatial-audio';
import {PlayerPrefs} from '../classes/PlayerPrefs.js';

const SOUND_ENABLED_KEY = 'SoundEnabled';

const sfx = 'audio/sfx/';

export class AudioDirector extends Component {
    static TypeName = 'audio-director';

    // Singleton
    private static _instance: AudioDirector;
    static get instance(): AudioDirector {
        return AudioDirector._instance;
    }

    private _soundEnabled: boolean = false;

    get soundEnabled(): boolean {
        return this._soundEnabled;
    }
    set soundEnabled(value: boolean) {
        if (this._soundEnabled !== value) {
            this._soundEnabled = value;

            PlayerPrefs.setBool(SOUND_ENABLED_KEY, value);
        }
    }

    private _time = 0;
    private _collapseTimeTolerance = 0.1;
    private _lastPlayedTime: Map<Sounds, number> = new Map();

    init() {
        if (AudioDirector._instance) {
            console.error('There can only be one instance of AudioDirector Component');
        }
        AudioDirector._instance = this;
    }

    start() {
        this._soundEnabled = PlayerPrefs.getBool(SOUND_ENABLED_KEY, true);
        globalAudioManager.loadBatch(
            [sfx + 'tnt_explode.mp3', Sounds.tntExplode],
            [
                [
                    sfx + 'blob_pop_1.mp3',
                    sfx + 'blob_pop_2.mp3',
                    sfx + 'blob_pop_3.mp3',
                    sfx + 'blob_pop_4.mp3',
                    sfx + 'blob_pop_5.mp3',
                    sfx + 'blob_pop_6.mp3',
                    sfx + 'blob_pop_7.mp3',
                    sfx + 'blob_pop_8.mp3',
                    sfx + 'blob_pop_9.mp3',
                    sfx + 'blob_pop_10.mp3',
                ],
                Sounds.blobPop,
            ],
            [
                [
                    sfx + 'blob_pop_max_1.mp3',
                    sfx + 'blob_pop_max_2.mp3',
                    sfx + 'blob_pop_max_3.mp3',
                ],
                Sounds.blobPopMax,
            ],
            [sfx + 'absorb.mp3', Sounds.blobAbsorb],
            [
                [
                    sfx + 'blob_exert_1.mp3',
                    sfx + 'blob_exert_2.mp3',
                    sfx + 'blob_exert_3.mp3',
                ],
                Sounds.blobExert,
            ],
            [
                [sfx + 'spit_1.mp3', sfx + 'spit_2.mp3', sfx + 'spit_3.mp3'],
                Sounds.blobSpitOut,
            ],
            [
                [sfx + 'hiccup_1.mp3', sfx + 'hiccup_2.mp3', sfx + 'hiccup_3.mp3'],
                Sounds.blobHiccup,
            ],
            [sfx + 'turntable.mp3', Sounds.TransitionToUrgent],
            [sfx + 'slowdown.mp3', Sounds.TransitionToNormal],
            [sfx + 'ready.mp3', Sounds.readySFX],
            [sfx + 'go.mp3', Sounds.goSFX],
            [sfx + 'kids_cheering.mp3', Sounds.newHighScoreSFX],
            [sfx + 'kids_cheering.mp3', Sounds.beatGameSFX],
            [
                [
                    sfx + 'fruit_splat_1.mp3',
                    sfx + 'fruit_splat_2.mp3',
                    sfx + 'fruit_splat_3.mp3',
                ],
                Sounds.fruitSplat,
            ],
            [sfx + 'whoosh.mp3', Sounds.whoosh],
            [sfx + 'fruit_land_slow.mp3', Sounds.fruitLandSlow],
            [sfx + 'fruit_land_fast.mp3', Sounds.fruitLandFast],
            [sfx + 'splash.mp3', Sounds.splash],
            [sfx + 'reload_throw.mp3', Sounds.launch],
            [sfx + 'reload.mp3', Sounds.duckDown],
            [sfx + 'sky_twinkle.mp3', Sounds.sparkle],
            [sfx + 'fruit_smasher.mp3', Sounds.fruitSmasher],
            [sfx + 'achievement_unlocked.mp3', Sounds.unlockSFX],
            [sfx + 'star_collect.mp3', Sounds.starCollect],
            [sfx + 'star_miss.mp3', Sounds.starMiss],
            [sfx + 'superstar_collect.mp3', Sounds.superStarCollect],
            [sfx + 'bounce.mp3', Sounds.bounce],
            [sfx + 'coin_collect.mp3', Sounds.coinCollect],
            [sfx + 'coconut_bomb_explode.mp3', Sounds.coconutBombExplode],
            [sfx + 'coconut_bomb_warning.mp3', Sounds.coconutBombWarning],
            [sfx + 'stack_rise.mp3', Sounds.moveUp],
            [sfx + 'level_up.mp3', Sounds.levelUp],
            [sfx + 'speed_up.mp3', Sounds.speedUp],
            [sfx + 'shape_bonus.mp3', Sounds.shapeBonus],
            [sfx + 'stack_pause_stinger.mp3', Sounds.lucky7],
            [sfx + 'six_string.mp3', Sounds.sixString],
            [sfx + 'magic_eight.mp3', Sounds.magic8],
            [sfx + 'blackjack.mp3', Sounds.blackJack],
            [sfx + 'game_over.mp3', Sounds.gameOver],
            [sfx + 'purchase.mp3', Sounds.successfulPurchase],
            [sfx + 'confirm.mp3', Sounds.confirm],
            [sfx + 'cancel.mp3', Sounds.cancel]
        );
    }

    update(dt: number) {
        this._time += dt;
    }

    playOneShotWithPriority(sound: Sounds) {
        if (!this.soundEnabled) {
            return;
        }

        const ga = globalAudioManager as AudioManager;
        try {
            ga.play(sound, {channel: 0, volume: 1, loop: false, priority: true});
        } catch (error) {
            console.warn(error);
        }
    }
    playOneShot(sound: Sounds, collapse: boolean);
    playOneShot(sound: Sounds, volume: number, collapse: boolean);
    playOneShot(sound: Sounds, collapseOrVolume: boolean | number, collapse?: boolean) {
        if (typeof collapseOrVolume === 'boolean') {
            collapse = collapseOrVolume;
        }
        let volume = 1;
        if (typeof collapseOrVolume === 'number') {
            volume = collapseOrVolume;
        }
        if (!this._validToPlay(sound, collapse)) {
            return;
        }
        const ga = globalAudioManager as AudioManager;

        try {
            ga.play(sound, {channel: 0, volume: volume, loop: false, priority: false});
            this._lastPlayedTime.set(sound, this._time);
        } catch (error) {
            console.warn(error);
        }
    }

    private _validToPlay(clip: Sounds, collapse: boolean): boolean {
        let playNow = true;
        if (!this._soundEnabled || clip == Sounds.none) {
            playNow = false;
        } else if (collapse) {
            const lastTime = this._lastPlayedTime.get(clip);
            if (lastTime && this._time - lastTime < this._collapseTimeTolerance) {
                playNow = false;
            }
        }
        return playNow;
    }
}
